import React from 'react';
import moment from 'moment';
import { ModalsKeys } from 'global';
import { useSelector } from 'react-redux';
import { useModal } from 'hooks/useModal';
import { useForm, useWatch } from 'react-hook-form';
import { Cancel, Save } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { accountSelectors } from 'redux/accountReducer';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { Box, Button, Stack, Typography, Divider } from '@mui/material';
import { UserProfile, DealActivityEventType, DealActivityEventPriority } from '@trnsact/trnsact-shared-types';
import {
  DateField,
  InputField,
  SelectField,
  CheckboxField,
  TimeSelectField,
  AutocompleteValueField,
} from 'components/form';

import { CalendarMapper } from '../../mapper';
import { useDealActivityMutations } from '../../../api';
import { calendarApi, useCalendarFormOptions } from '../../api';
import {
  createEventFormSchema,
  CreateEventFormValues,
  getDefaultEventTimes,
  getCreateEventFormDefaultValues,
} from './createEventFormSchema';

export const CreateEventDialog = () => {
  const { isOpen, handleClose, data: dialogData } = useModal(ModalsKeys.CreateEventDialog);
  const { handleCreateDealActivity, isDealActivityCreating } = useDealActivityMutations();

  const userProfile = useSelector<any, UserProfile>(state => state.userProfile);
  const currentUserAccountId = useSelector(accountSelectors.accountId);

  const { control, setValue, handleSubmit, reset } = useForm<CreateEventFormValues>({
    resolver: zodResolver(createEventFormSchema),
    defaultValues: getCreateEventFormDefaultValues(userProfile.fullName ?? '', dialogData?.accountId),
  });

  const [accountId, allDay, startDate] = useWatch({ control, name: ['accountId', 'allDay', 'startDate'] });

  const { accountOptions, contactOptions, accountOptionsLoading, contactOptionsLoading } =
    useCalendarFormOptions(accountId);

  const onSubmit = async (data: CreateEventFormValues) => {
    await handleCreateDealActivity(CalendarMapper.mapFormDtoToCreate(data, currentUserAccountId), () => ({
      awaitRefetchQueries: true,
      refetchQueries: [calendarApi.events.queries.listing, calendarApi.events.queries.listWithActivities],
    }));

    reset(getCreateEventFormDefaultValues(userProfile.fullName ?? '', dialogData?.accountId));
    handleClose();
  };

  const handleDialogClose = () => {
    reset(getCreateEventFormDefaultValues(userProfile.fullName ?? '', dialogData?.accountId));
    handleClose();
  };

  return (
    <CustomDialog
      maxWidth="md"
      isOpen={isOpen}
      label="New Event"
      disableBackdropClose
      onClose={handleDialogClose}
      actions={
        <Stack direction="row" gap="1rem" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
            loading={isDealActivityCreating}
          >
            Create
          </Button>

          <Button onClick={handleDialogClose} startIcon={<Cancel />} disabled={isDealActivityCreating}>
            Cancel
          </Button>
        </Stack>
      }
    >
      <Stack gap="1rem">
        <Stack gap="0.5rem">
          <Typography>Calendar Details</Typography>

          <Stack height="100%" direction="column" gap="1rem">
            <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
              <InputField
                label="Subject"
                name="subject"
                control={control}
                inputProps={{ fullWidth: true, variant: 'outlined', required: true }}
              />

              <InputField
                name="assignedTo"
                control={control}
                label="Assigned To"
                inputProps={{ disabled: true, fullWidth: true, variant: 'outlined', required: true }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
              <SelectField
                required
                label="Type"
                name="eventType"
                control={control}
                variant="outlined"
                options={convertEnumToSelectOptions(DealActivityEventType)}
              />

              <SelectField
                required
                name="priority"
                label="Priority"
                control={control}
                variant="outlined"
                options={convertEnumToSelectOptions(DealActivityEventPriority)}
              />
            </Box>

            <Box>
              <CheckboxField
                label="All day"
                name="allDay"
                control={control}
                extraAction={checked => {
                  if (checked) {
                    setValue('startTime', null);
                    setValue('endTime', null);
                  } else {
                    const { startTime, endTime } = getDefaultEventTimes();

                    setValue('startTime', startTime);
                    setValue('endTime', endTime);
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: allDay ? '1fr 1fr' : '1fr 1fr 1fr 1fr' }}>
              <DateField
                name="startDate"
                control={control}
                label="Start Date"
                slotProps={{ textField: { size: 'small', required: true, variant: 'outlined' } }}
                extraAction={value => {
                  if (!value) return;

                  setValue('endDate', moment(value).toISOString());
                }}
              />

              {!allDay && (
                <TimeSelectField
                  required
                  size="small"
                  name="startTime"
                  control={control}
                  label="Start Time"
                  variant="outlined"
                  extraAction={value => {
                    if (!value) return;

                    const { time, nextDay } = CalendarMapper.addOneHour(value);

                    setValue('endTime', time);

                    if (nextDay && startDate) {
                      setValue('endDate', moment(startDate).add(1, 'day').toISOString());
                    }
                  }}
                />
              )}

              <DateField
                name="endDate"
                control={control}
                label="End Date"
                minDate={startDate ? moment(startDate) : undefined}
                slotProps={{ textField: { variant: 'outlined', size: 'small', required: true } }}
              />

              {!allDay && (
                <TimeSelectField
                  required
                  size="small"
                  name="endTime"
                  label="End Time"
                  control={control}
                  variant="outlined"
                />
              )}
            </Box>

            <InputField
              label="Description"
              name="description"
              control={control}
              inputProps={{
                minRows: 3,
                multiline: true,
                fullWidth: true,
                variant: 'outlined',
                placeholder: 'Add description...',
              }}
            />
          </Stack>
        </Stack>

        <Divider />

        <Stack gap="0.5rem">
          <Typography>Related To (Contact)</Typography>

          <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
            <AutocompleteValueField
              autoHighlight
              name="accountId"
              control={control}
              options={accountOptions}
              loading={accountOptionsLoading}
              filterOptions={createFilterOptions({ limit: 100 })}
              extraAction={() => setValue('contactId', '')}
              textFieldProps={{ label: 'Account', variant: 'outlined', size: 'small', required: true }}
            />

            <AutocompleteValueField
              name="contactId"
              control={control}
              disabled={!accountId}
              options={contactOptions}
              loading={contactOptionsLoading}
              textFieldProps={{ label: 'Contact', variant: 'outlined', size: 'small' }}
            />
          </Box>
        </Stack>
      </Stack>
    </CustomDialog>
  );
};
